// Dynamic configuration from environment variables
// These are set during build time based on .env files
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api1.masternode.wobot.ai";
export const FRONTEND_VERSION = process.env.REACT_APP_FRONTEND_VERSION || "1.0.0";
export const API_VERSION = parseInt(process.env.REACT_APP_API_VERSION || "2", 10);
export const REQUIRED_API_VERSION = API_VERSION;
export const APP_ENV = process.env.REACT_APP_ENV || process.env.NODE_ENV || "production";

// Log configuration on app load (only in development)
if (process.env.NODE_ENV === 'development') {
    console.log('🔧 API Configuration:', {
        API_BASE_URL,
        FRONTEND_VERSION,
        API_VERSION,
        APP_ENV
    });
}

// Basic error handling helper
const handleResponse = async (response) => {
    if (!response.ok) {
        let errorData;
        try {
            errorData = await response.json();
        } catch (e) {
            // If response is not JSON
            errorData = { error: response.statusText || 'Unknown server error' };
        }
        const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = errorData;
        throw error;
    }
    // Handle cases where server returns 2xx but no content (like DELETE)
    if (response.status === 204 || response.status === 202) {
         return { status: 'success', statusCode: response.status };
    }
    return response.json();
};

const request = async (url, options) => {
    try {
        // Prepend the base URL to all requests
        const fullUrl = `${API_BASE_URL}${url}`;
        const response = await fetch(fullUrl, options);
        return await handleResponse(response);
    } catch (error) {
        // Network errors or errors from handleResponse
        console.error("API Request Error:", error);
        throw error; // Re-throw the error to be caught by the calling component
    }
};

const getHeaders = (apiKey) => {
    const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    };

    if (apiKey) {
        headers['X-API-Key'] = apiKey;
    }

    return headers;
};
const getAdminHeaders = (adminToken) => {
    const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${adminToken}`,
    };

    return headers;
};


// --- WebSocket Status API ---

// Get server compatibility and version information
export const getServerMeta = () => {
    return request('/meta', {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
        }
    });
};

export const pollRequestStatus = (requestID, handshakeKey) => {
    return request(`/ws/status/${requestID}`, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'X-Handshake-Key': handshakeKey
        }
    });
};

// --- Client API Functions ---

// buildClientListUrl builds the URL for client list API with filter parameters
// Supports pagination and advanced filtering: generic search, name, clientID, partnerName, company, location, status
const buildClientListUrl = (page = 1, limit = 10, filters = {}) => {
    const params = new URLSearchParams();
    
    params.append('page', page);
    params.append('limit', limit);
    
    // Add filter parameters if provided
    if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim());
    }
    if (filters.name && filters.name.trim()) {
        params.append('name', filters.name.trim());
    }
    if (filters.clientID && filters.clientID.trim()) {
        params.append('clientID', filters.clientID.trim());
    }
    if (filters.partnerName && filters.partnerName.trim()) {
        params.append('partnerName', filters.partnerName.trim());
    }
    if (filters.company && filters.company.trim()) {
        params.append('company', filters.company.trim());
    }
    if (filters.location && filters.location.trim()) {
        params.append('location', filters.location.trim());
    }
    if (filters.status && filters.status.trim()) {
        params.append('status', filters.status.trim());
    }
    
    return `/api/clients?${params.toString()}`;
};

// getClients retrieves clients with pagination and optional filtering
// filters object can contain: search, name, clientID, partnerName, company, location, status
export const getClients = (adminToken, page = 1, limit = 10, filters = {}) => {
    const url = buildClientListUrl(page, limit, filters);
    
    return request(url, {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

export const getApprovedClients = (adminToken) => {
    return request('/api/approvedclients', {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

export const getClientDetails = (adminToken, clientId) => {
    return request(`/api/clients/${clientId}`, {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

export const getClientPaths = (adminToken, clientId) => {
    return request(`/api/clients/${clientId}/paths`, {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

export const setClientName = (adminToken, clientId, name) => {
    return request(`/api/clients/${clientId}/name`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
        body: JSON.stringify({ name }),
    });
};

export const updateClient = (adminToken, clientId, clientData) => {
    return request(`/api/clients/${clientId}`, {
        method: 'PUT',
        headers: getAdminHeaders(adminToken),
        body: JSON.stringify(clientData),
    });
};

export const addOrUpdatePath = (adminToken, clientId, pathName, config) => {
    const payload = {
        pathName: pathName,
        config: {
            runOnInit: config.runOnInit || "",
            runOnInitRestart: config.runOnInitRestart || "no"
        }
    };
     return request(`/api/clients/${clientId}/paths`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
        body: JSON.stringify(payload),
    });
};

export const deletePath = (adminToken, clientId, pathName) => {
    const encodedPathName = encodeURIComponent(pathName);
    return request(`/api/clients/${clientId}/paths/${encodedPathName}`, {
        method: 'DELETE',
        headers: getAdminHeaders(adminToken),
    });
};

export const triggerReload = (adminToken, clientId) => {
    return request(`/api/clients/${clientId}/reload`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
    });
};

export const triggerUpdate = (adminToken, clientId) => {
    return request(`/api/clients/${clientId}/update`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
    });
};

export const bulkTriggerUpdate = (adminToken, clientIds) => {
    return request(`/api/clients/bulk-update`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
        body: JSON.stringify({ clientIds }),
    });
};

export const getUpdateStatus = (adminToken, clientId) => {
    return request(`/api/clients/${clientId}/update-status`, {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

export const generateSystemReport = (adminToken, clientId) => {
    return request(`/api/clients/${clientId}/generate-report`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
    });
};

export const deleteClient = (adminToken, clientId) => {
    return request(`/api/clients/${clientId}`, {
        method: 'DELETE',
        headers: getAdminHeaders(adminToken),
    });
};

export const blockClient = (adminToken, clientId) => {
    return request(`/api/clients/${clientId}/block`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
    });
};

export const unblockClient = (adminToken, clientId) => {
    return request(`/api/clients/${clientId}/block`, {
        method: 'DELETE',
        headers: getAdminHeaders(adminToken),
    });
};

// --- Metrics API ---

export const getClientsStatusMetrics = (adminToken) => {
    return request('/api/metrics/clients/status', {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

// --- Partner Management API (renamed from User Groups) ---

export const getPartners = (adminToken) => {
    return request('/api/partners', {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

export const createPartner = (adminToken, name) => {
    return request('/api/partners', {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
        body: JSON.stringify({ name }),
    });
};

export const deletePartner = (adminToken, partnerName) => {
    return request(`/api/partners/${partnerName}`, {
        method: 'DELETE',
        headers: getAdminHeaders(adminToken),
    });
};

export const regeneratePartnerKey = (adminToken, partnerName) => {
    return request(`/api/partners/${partnerName}/regenerate-key`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
    });
};

export const getPartnerKeyDetails = (adminToken, partnerName) => {
    return request(`/api/partners/${partnerName}/key-details`, {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

// --- User Management API (RBAC) ---

export const getCurrentUser = (adminToken) => {
    return request('/api/users/me', {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

export const getUsers = (adminToken) => {
    return request('/api/users', {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

export const createUser = (adminToken, userData) => {
    return request('/api/users', {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
        body: JSON.stringify(userData),
    });
};

export const getUser = (adminToken, userId) => {
    return request(`/api/users/${userId}`, {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

export const updateUser = (adminToken, userId, userData) => {
    return request(`/api/users/${userId}`, {
        method: 'PUT',
        headers: getAdminHeaders(adminToken),
        body: JSON.stringify(userData),
    });
};

export const deleteUser = (adminToken, userId) => {
    return request(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: getAdminHeaders(adminToken),
    });
};

export const regenerateUserAPIKey = (adminToken, userId) => {
    return request(`/api/users/${userId}/regenerate-key`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
    });
};

// --- Pending Request Management API ---

export const getPendingRequests = (adminToken) => {
    return request('/api/requests/pending', {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

export const approveRequest = (adminToken, requestId, name = null) => {
    const body = name ? JSON.stringify({ name }) : '{}';
    return request(`/api/requests/${requestId}/approve`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
        body: body,
    });
};

export const rejectRequest = (adminToken, requestId) => {
    return request(`/api/requests/${requestId}/reject`, {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
    });
};

// --- Admin Key Management API ---

export const checkAdminKey = (adminToken) => {
    return request('/api/debug/admin-key', {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

export const resetAdminKey = (apiKey) => {
    return request('/api/reset-admin-key', {
        method: 'POST',
        headers: getHeaders(apiKey),
    });
};

export const regenerateKey = (adminToken) => {
    return request('//regenerate-key', {
        method: 'POST',
        headers: getAdminHeaders(adminToken),
    });
};


// --- Tunnel Management API ---

export const getTunnels = (adminToken) => {
    return request('/api/tunnels', {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};

export const deleteTunnel = (adminToken, tunnelId) => {
    return request(`/api/tunnels/${tunnelId}`, {
        method: 'DELETE',
        headers: getAdminHeaders(adminToken),
    });
};

export const getTunnelDetails = (adminToken, tunnelId) => {
    return request(`/api/tunnels/${tunnelId}`, {
        method: 'GET',
        headers: getAdminHeaders(adminToken),
    });
};